import first from 'lodash/first';
import last from 'lodash/last';
import BASKET_PAYMENT_MODES from '@constants/basketPaymentModes';
import DIET_TYPE from '@constants/dietType';
import GTM_ITEM_XML_CATEGORIES from '@contexts/hooks/constants/gtmItemXmlCategories';
import getRoundedPrice from '@contexts/hooks/utils/getRoundedPrice';
import { format } from '@services/Date.service';
import getDeliveryCost from '@utils/getDeliveryCost';
import { getIntegerIfIntegerOrFixed } from '@utils/helpers';

export const pushEventToDataLayer = event => {
  if (!window?.dataLayer) {
    return;
  }
  window.dataLayer.push({ ...event });
};

const getGtmItems = ({ items, brandName, discountCode }) =>
  items.reduce(
    (acc, item, index) => [
      ...acc,
      // eslint-disable-next-line no-use-before-define -- FIXME
      ...convertToGtmItem({
        index,
        item,
        brandName,
        discountCode,
      }),
    ],
    []
  );

export const pushLogoGTMEvent = pagePath => {
  pushEventToDataLayer({
    event: 'logo',
    pagePath,
  });
};

export const pushLoginGTMEvent = ({ provider, ctaType, pagePath, userId }) => {
  pushEventToDataLayer({
    event: 'login',
    method: provider,
    action: ctaType,
    pagePath,
    userId,
  });
};

export const pushRegisterGTMEvent = ({
  provider,
  ctaType,
  pagePath,
  userId,
}) => {
  pushEventToDataLayer({
    event: 'register',
    method: provider,
    action: ctaType,
    pagePath,
    userId,
  });
};

export const pushCheckoutStepGTMEvent = ({ step }) => {
  pushEventToDataLayer({
    event: 'checkoutStep',
    step,
  });
};

export const pushPageViewGTMEvent = linkToPage => {
  pushEventToDataLayer({
    event: 'pageView',
    link: linkToPage,
  });
};

export const pushClickMenuGTMEvent = ({
  text,
  action = 'click',
  level = 'sub0',
}) => {
  pushEventToDataLayer({
    event: 'menu',
    text,
    // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
    action, // click | dropdown menu
    // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
    level, // sub0 | sub1 | sub2
  });
};

const convertAddonToGtmItem = ({ brandName, index, item }) => ({
  // eslint-disable-next-line camelcase -- FIXME
  item_id: item?.addon?.id?.toString() || '',
  // eslint-disable-next-line camelcase -- FIXME
  item_xml_id: item?.addon?.id
    ? `${GTM_ITEM_XML_CATEGORIES.addon}_${item.addon.id}`
    : '',
  // eslint-disable-next-line camelcase -- FIXME
  item_name: item?.addon?.name || '',
  coupon: '',
  discount: item?.price?.discount || 0,
  index,
  // eslint-disable-next-line camelcase -- FIXME
  item_brand: brandName,
  // eslint-disable-next-line camelcase -- FIXME
  item_category: 'Dodatek',
  // eslint-disable-next-line camelcase -- FIXME
  item_category2: '',
  // eslint-disable-next-line camelcase -- FIXME
  item_category3: '',
  // eslint-disable-next-line camelcase -- FIXME
  item_category4: '',
  // eslint-disable-next-line camelcase -- FIXME
  item_category5: '',
  // eslint-disable-next-line camelcase -- FIXME
  item_category6: false,
  // eslint-disable-next-line camelcase -- FIXME
  item_category7: 0,
  // eslint-disable-next-line camelcase -- FIXME
  item_category8: '',
  // eslint-disable-next-line camelcase -- FIXME
  item_category9: '',
  // eslint-disable-next-line camelcase -- FIXME
  item_category10: '',
  // eslint-disable-next-line camelcase -- FIXME
  item_category11: '',
  // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
  item_category12: item?.type, // typ dostawy dodatku
  // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
  price: parseFloat(getIntegerIfIntegerOrFixed(item?.addon?.clientCost || 0)), // TODO: OrderItemDiet - API
  quantity: item?.quantity || 0,
});

const convertToGtmItem = ({ brandName, discountCode, index, item }) => {
  switch (item['@type']) {
    case 'BasketItemDiet': {
      const isSubscriptionPayment =
        item?.paymentMode === BASKET_PAYMENT_MODES.SUBSCRIPTION_PAYMENT;

      const addons = (item?.addons || []).reduce(
        // eslint-disable-next-line no-shadow -- FIXME
        (acc, item, index) => [
          ...acc,
          convertAddonToGtmItem({
            brandName,
            index,
            item,
          }),
        ],
        []
      );

      return [
        {
          // eslint-disable-next-line camelcase -- FIXME
          item_id: (item?.diet?.id || item?.id)?.toString() || '',
          // eslint-disable-next-line camelcase -- FIXME
          item_xml_id:
            item?.diet?.id && item?.variant?.id && item?.calorific?.id
              ? `${GTM_ITEM_XML_CATEGORIES.diet}_${item.diet.id}_${item.variant.id}_${item.calorific.id}`
              : '',
          // eslint-disable-next-line camelcase -- FIXME
          item_name: item?.diet?.name || item?.name || '',
          coupon: discountCode || '',
          discount: item?.price?.discount || 0,
          index,
          // eslint-disable-next-line camelcase -- FIXME
          item_brand: brandName,
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category: 'Dieta', // typ elementu Dieta | Danie
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category2: item?.diet?.name || item?.name || '', // nazwa diety - Wege
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category3: item?.variant?.name || item?.variantName || '', // wariant diety - 3 posiłki
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category4: item?.calorific?.name || item?.calorificName || '', // kaloryczność - ok. 5kg - 400kcal
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category5: '', // Wielkość dania
          // eslint-disable-next-line camelcase -- FIXME
          item_category6: isSubscriptionPayment,
          // eslint-disable-next-line camelcase -- FIXME
          item_category7: parseFloat(
            getIntegerIfIntegerOrFixed(
              (item.price.priceParts?.DIET ?? item.price.afterDiscount) /
                item.dietLength
            )
            // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
          ), // cena za dzień
          // eslint-disable-next-line camelcase -- FIXME
          item_category8:
            item?.options?.saturdayInclude || item?.saturdayInclude || '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category9:
            item?.options?.sundayInclude || item?.sundayInclude || '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category10: (first(item?.days) ?? item?.firstDeliveryDay) || '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category11:
            (isSubscriptionPayment
              ? ''
              : last(item?.days ?? item?.deliveryDates)) || '',
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category12: '', // typ dostawy dodatku
          price: getRoundedPrice(
            ((item?.price?.afterDiscount || 0) -
              (item?.price?.priceParts?.ADDON || 0)) /
              (item?.dietLength || 1)
          ),
          quantity: item?.quantity || 0,
          duration: item?.dietLength || 0,
        },
        ...addons,
      ];
    }

    case 'BasketItemDish':
      return [
        {
          // eslint-disable-next-line camelcase -- FIXME
          item_id: item?.dishSize?.id?.toString() || '',
          // eslint-disable-next-line camelcase -- FIXME
          item_xml_id: item?.dishSize?.id
            ? `${GTM_ITEM_XML_CATEGORIES.dish}_${item.dishSize.id}`
            : '',
          // eslint-disable-next-line camelcase -- FIXME
          item_name: item?.dishSize?.dish?.nameForClient || '',
          coupon: discountCode || '',
          discount: item?.price?.discount || 0,
          index,
          // eslint-disable-next-line camelcase -- FIXME
          item_brand: brandName,
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category: 'Danie', // typ elementu Dieta | Danie
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category2: '', // nazwa diety - Wege
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category3: '', // wariant diety - 3 posiłki
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category4: '', // kaloryczność - ok. 5kg - 400kcal
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category5: item?.dishSize?.size?.nameForClient || '', // Wielkość dania
          // eslint-disable-next-line camelcase -- FIXME
          item_category6: false,
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category7: 0, // cena za dzień
          // eslint-disable-next-line camelcase -- FIXME
          item_category8: '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category9: '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category10: item?.day,
          // eslint-disable-next-line camelcase -- FIXME
          item_category11: item?.day,
          // eslint-disable-next-line camelcase -- FIXME
          item_category12: '',
          price: getRoundedPrice(
            (item?.price?.afterDiscount || 0) / (item?.quantity || 1)
          ),
          quantity: item?.quantity || 0,
        },
      ];

    case 'OrderItemDayDish': {
      const dishSize = item?.dishSize;

      return [
        {
          // eslint-disable-next-line camelcase -- FIXME
          item_id: dishSize ? dishSize.id.toString() : '',
          // eslint-disable-next-line camelcase -- FIXME
          item_name: dishSize ? dishSize.name : '',
          // eslint-disable-next-line camelcase -- FIXME
          item_xml_id: dishSize
            ? `${GTM_ITEM_XML_CATEGORIES.dish}_${dishSize.id}`
            : '',
          coupon: discountCode || '',
          discount: item?.priceV2?.discount || 0,
          index,
          // eslint-disable-next-line camelcase -- FIXME
          item_brand: brandName,
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category: 'Danie', // typ elementu Dieta | Danie
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category2: '', // nazwa diety - Wege
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category3: '', // wariant diety - 3 posiłki
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category4: '', // kaloryczność - ok. 5kg - 400kcal
          // eslint-disable-next-line camelcase -- FIXME
          item_category6: false,
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category7: 0, // cena za dzień
          // eslint-disable-next-line camelcase -- FIXME
          item_category8: '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category9: '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category10: format(new Date(item?.day)),
          // eslint-disable-next-line camelcase -- FIXME
          item_category11: format(new Date(item?.day)),
          // eslint-disable-next-line camelcase -- FIXME
          item_category12: '',
          price: getRoundedPrice(
            (item?.priceV2?.afterDiscount || 0) / (item?.quantity || 1)
          ),
          quantity: item?.quantity || 0,
        },
      ];
    }

    case 'OrderItemDayAddon': {
      const addon = item?.purchasedAddon.addon;

      return [
        {
          // eslint-disable-next-line camelcase -- FIXME
          item_id: addon ? addon.id.toString() : '',
          // eslint-disable-next-line camelcase -- FIXME
          item_name: addon ? addon.name : '',
          // eslint-disable-next-line camelcase -- FIXME
          item_xml_id: addon
            ? `${GTM_ITEM_XML_CATEGORIES.addon}_${addon.id}`
            : '',
          coupon: discountCode || '',
          discount: item?.priceV2?.discount || 0,
          index,
          // eslint-disable-next-line camelcase -- FIXME
          item_brand: brandName,
          // eslint-disable-next-line camelcase -- FIXME
          item_category: 'Dodatek',
          // eslint-disable-next-line camelcase -- FIXME
          item_category2: '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category3: '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category4: '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category6: false,
          // eslint-disable-next-line camelcase -- FIXME
          item_category7: 0,
          // eslint-disable-next-line camelcase -- FIXME
          item_category8: '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category9: '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category10: format(new Date(item?.day)),
          // eslint-disable-next-line camelcase -- FIXME
          item_category11: format(new Date(item?.day)),
          // eslint-disable-next-line camelcase -- FIXME
          item_category12: '',
          price: getRoundedPrice(
            (item?.priceV2?.afterDiscount || 0) / (item?.quantity || 1)
          ),
          quantity: item?.quantity || 0,
        },
      ];
    }

    case 'BasketItemAddon':
      return [
        {
          // eslint-disable-next-line camelcase -- FIXME
          item_id: item?.addon?.id?.toString() || '',
          // eslint-disable-next-line camelcase -- FIXME
          item_xml_id: item?.addon?.id
            ? `${GTM_ITEM_XML_CATEGORIES.addon}_${item.addon.id}`
            : '',
          // eslint-disable-next-line camelcase -- FIXME
          item_name: item?.addon?.name || '',
          coupon: discountCode || '',
          discount: item?.price?.discount || 0,
          index,
          // eslint-disable-next-line camelcase -- FIXME
          item_brand: brandName,
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category: 'Dodatek', // typ elementu Dieta | Danie
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category2: '', // nazwa diety - Wege
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category3: '', // wariant diety - 3 posiłki
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category4: '', // kaloryczność - ok. 5kg - 400kcal
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category5: '', // Wielkość dania
          // eslint-disable-next-line camelcase -- FIXME
          item_category6: false,
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category7: 0, // cena za dzień
          // eslint-disable-next-line camelcase -- FIXME
          item_category8: '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category9: '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category10: item?.day,
          // eslint-disable-next-line camelcase -- FIXME
          item_category11: item?.day,
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category12: item?.addon?.type, // typ dostawy dodatku
          price: getRoundedPrice(
            (item?.price?.afterDiscount || 0) / (item?.quantity || 1)
          ),
          quantity: item?.quantity || 0,
        },
      ];

    case DIET_TYPE.OrderItemDiet: {
      const discountPrice = item?.priceV2?.discount || 0;
      const isSubscriptionPayment =
        item?.paymentMode === BASKET_PAYMENT_MODES.SUBSCRIPTION_PAYMENT;

      const addons = (item?.addons || []).reduce(
        // eslint-disable-next-line no-shadow -- FIXME
        (acc, item, index) => [
          ...acc,
          convertAddonToGtmItem({
            brandName,
            index,
            item,
          }),
        ],
        []
      );

      return [
        {
          // eslint-disable-next-line camelcase -- FIXME
          item_id: item?.offerDiet?.id?.toString() || '',
          // eslint-disable-next-line camelcase -- FIXME
          item_xml_id:
            item?.offerDiet?.id && item?.variant?.id && item?.calorific?.id
              ? `${GTM_ITEM_XML_CATEGORIES.diet}_${item.offerDiet.id}_${item.variant.id}_${item.calorific.id}`
              : '',
          // eslint-disable-next-line camelcase -- FIXME
          item_name: item?.offerDiet?.name || '',
          coupon: discountCode || '',
          discount: discountPrice,
          index,
          // eslint-disable-next-line camelcase -- FIXME
          item_brand: brandName,
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category: 'Dieta', // typ elementu Dieta | Danie
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category2: item?.offerDiet?.name || '', // nazwa diety - Wege
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category3: item?.variant?.name || '', // wariant diety - 3 posiłki
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category4: item?.calorific?.name || '', // kaloryczność - ok. 5kg - 400kcal
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category5: '', // Wielkość dania
          // eslint-disable-next-line camelcase -- FIXME
          item_category6: isSubscriptionPayment,
          // eslint-disable-next-line camelcase -- FIXME
          item_category7: parseFloat(
            getIntegerIfIntegerOrFixed(
              item?.priceV2?.afterDiscount / (item?.days?.length || 0)
            )
            // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
          ), // cena za dzień
          // eslint-disable-next-line camelcase -- FIXME
          item_category8: item?.dietOptions?.saturdayInclude || '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category9: item?.dietOptions?.sundayInclude || '',
          // eslint-disable-next-line camelcase -- FIXME
          item_category10: first(item?.days),
          // eslint-disable-next-line camelcase -- FIXME
          item_category11: isSubscriptionPayment ? '' : last(item?.days),
          // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
          item_category12: '', // typ dostawy dodatku
          price: getRoundedPrice(
            ((item?.priceV2?.afterDiscount || 0) -
              (item?.priceV2?.priceParts?.ADDON || 0)) /
              (item?.days?.length || 1)
          ),
          quantity: 1,
          duration: item?.days?.length || 0,
        },
        ...addons,
      ];
    }

    default: {
      return [];
    }
  }
};

export const pushViewItemGTMEvent = ({
  brandName,
  currencyCode,
  discountCode = '',
  items = [],
  price = 0,
}) => {
  pushEventToDataLayer({
    event: 'view_item',
    ecommerce: {
      currency: currencyCode,
      value: price,
      items: getGtmItems({ items, brandName, discountCode }),
    },
  });
};

export const pushAddShippingInfoGTMEvent = ({
  brandName,
  currencyCode,
  discountCode = '',
  items = [],
  price = 0,
  shipping,
}) => {
  pushEventToDataLayer({
    event: 'add_shipping_info',
    ecommerce: {
      currency: currencyCode,
      value: price,
      coupon: discountCode,
      // eslint-disable-next-line camelcase -- FIXME
      shipping_tier: shipping,
      items: getGtmItems({ items, brandName, discountCode }),
    },
  });
};

export const pushViewCartGTMEvent = ({
  brandName,
  currencyCode,
  discountCode = '',
  items = [],
  price = 0,
}) => {
  pushEventToDataLayer({
    event: 'view_cart',
    ecommerce: {
      currency: currencyCode,
      value: price,
      items: getGtmItems({ items, brandName, discountCode }),
    },
  });
};

export const pushViewCartNewGTMEvent = ({
  brandName,
  currencyCode,
  discountCode = '',
  items = [],
  price = 0,
}) => {
  pushEventToDataLayer({
    event: 'view_cart_new',
    ecommerce: {
      currency: currencyCode,
      value: price,
      items: getGtmItems({ items, brandName, discountCode }),
    },
  });
};

export const pushAddPaymentInfoGTMEvent = ({
  brandName,
  currencyCode,
  discountCode = '',
  items = [],
  paymentType,
  price = 0,
}) => {
  pushEventToDataLayer({
    event: 'add_payment_info',
    ecommerce: {
      currency: currencyCode,
      value: price,
      coupon: discountCode,
      // eslint-disable-next-line camelcase -- FIXME
      payment_type: paymentType,
      items: getGtmItems({ items, brandName, discountCode }),
    },
  });
};

export const pushBeginCheckoutGTMEvent = ({
  brandName,
  currencyCode,
  discountCode = '',
  items = [],
  price = 0,
}) => {
  pushEventToDataLayer({
    event: 'begin_checkout',
    ecommerce: {
      currency: currencyCode,
      value: price,
      coupon: discountCode,
      items: getGtmItems({ items, brandName, discountCode }),
    },
  });
};

export const pushBeginPurchaseGTMEvent = ({
  brandName,
  currencyCode,
  discountCode = '',
  items = [],
  price = 0,
  paymentType = '',
  shippingCost = 0,
  transactionId = '',
}) => {
  const isSubscription = items.some(
    item => item.paymentMode === BASKET_PAYMENT_MODES.SUBSCRIPTION_PAYMENT
  );

  pushEventToDataLayer({
    event: 'begin_purchase',
    ecommerce: {
      currency: currencyCode,
      value: price,
      coupon: discountCode,
      // eslint-disable-next-line camelcase -- FIXME
      payment_type: isSubscription ? `${paymentType}_SUB` : paymentType || '',
      shipping: shippingCost,
      // eslint-disable-next-line camelcase -- FIXME
      transaction_id: transactionId.toString(),
      items: getGtmItems({ items, brandName, discountCode }),
    },
  });
};

export const pushPurchaseGTMEvent = ({
  brandName,
  currencyCode,
  orderDetails,
  eventName = 'purchase',
}) => {
  const {
    id,
    items,
    discountCode,
    paymentType,
    priceAfterDiscount,
    priceFromMoneyBox,
    isTest,
  } = orderDetails;

  const value =
    (Number(priceAfterDiscount) || 0) - (Number(priceFromMoneyBox) || 0);

  const shippingCost = items.reduce(
    (acc, { priceV2 }) => acc + getDeliveryCost(priceV2),
    0
  );

  const isBagModification = items.some(item => item.type === 'BagModification');

  const isSubscription = items.some(
    item => item.paymentMode === BASKET_PAYMENT_MODES.SUBSCRIPTION_PAYMENT
  );

  const event = {
    event: eventName,
    ecommerce: {
      // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
      currency: currencyCode, // REQUIRED
      // eslint-disable-next-line camelcase, @stylistic/line-comment-position -- FIXME
      transaction_id: id?.toString(), // REQUIRED
      // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
      value, // REQUIRED
      coupon: discountCode?.code || '',
      shipping: shippingCost,
      // tax: 0,
      // eslint-disable-next-line camelcase -- FIXME
      payment_type: isSubscription ? `${paymentType}_SUB` : paymentType || '',
      // eslint-disable-next-line no-negated-condition -- FIXME
      items: !isBagModification
        ? items.reduce(
            (acc, item, index) => [
              ...acc,
              ...convertToGtmItem({
                index,
                item,
                brandName,
                discountCode: discountCode?.code || '',
              }),
            ],
            []
          )
        : // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
          [], // 'Bag/s Modification',
      testMode: isTest,
    },
  };

  pushEventToDataLayer(event);
};

export const pushPrePurchaseGTMEvent = ({
  brandName,
  currencyCode,
  orderDetails,
}) => {
  pushPurchaseGTMEvent({
    brandName,
    currencyCode,
    orderDetails,
    eventName: 'prePurchase',
  });
};

export const pushDiscountCodeUpdateGTMEvent = ({
  discountCode = null,
  items = [],
  brandName = '',
  currencyCode,
  price = 0,
} = {}) => {
  pushEventToDataLayer({
    event: 'update_discount_code',
    discountCode,
    ecommerce: {
      currency: currencyCode,
      value: price,
      items: getGtmItems({ items, brandName, discountCode }),
    },
  });
};

export const pushUIDGTMEvent = ({ userId, email }) => {
  pushEventToDataLayer({
    event: 'UID',
    email,
    userId,
  });
};
